import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../apiClient";
import { SETTINGS_STORAGE_KEY, TOKEN_STORAGE_KEY } from "../config";
import { t } from "../i18n";

export default function useSkeletonPageState({ onSignOut, session, onSessionUpdate }) {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [updateNotification, setUpdateNotification] = useState(null);
  const [changePassOpen, setChangePassOpen] = useState(false);
  const [confirmChangeOpen, setConfirmChangeOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [cardScale, setCardScale] = useState(session?.settings?.cardScale || 1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState("general");
  const [adminSectionInitialized, setAdminSectionInitialized] = useState(false);
  const [users, setUsers] = useState([]);
  const [awgProfiles, setAwgProfiles] = useState([]);
  const [awgProfilesLoading, setAwgProfilesLoading] = useState(false);
  const [awgProfilesLoadedOnce, setAwgProfilesLoadedOnce] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ open: false, displayName: "", email: "", role: "user", password: "", confirmPassword: "", sendInvitation: false });
  const [passwordDialog, setPasswordDialog] = useState({ open: false, email: "", password: "", confirmPassword: "" });
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, email: "" });
  const [awgCreateDialog, setAwgCreateDialog] = useState({ open: false, profileName: "", displayName: "", email: "", sendEmail: false });
  const [awgEditDialog, setAwgEditDialog] = useState({ open: false, profileName: "", displayName: "", email: "" });
  const [awgDeleteDialog, setAwgDeleteDialog] = useState({ open: false, profileName: "" });
  const [awgEventsDialog, setAwgEventsDialog] = useState({ open: false, profileName: "", items: [], periodHours: 24 });
  const [awgShareDialog, setAwgShareDialog] = useState({ open: false, profileName: "", displayName: "", email: "" });
  const [awgAnalyticsDialog, setAwgAnalyticsDialog] = useState({ open: false, profile: null, data: null, loading: false, periodHours: "all" });
  const initialAnalyticsPeriod = String(session?.settings?.analyticsPeriodHours || "all");
  const [analyticsDashboardVisible, setAnalyticsDashboardVisible] = useState(session?.settings?.analyticsDashboardVisible !== false);
  const [awgAnalyticsSummary, setAwgAnalyticsSummary] = useState({ enabled: true, panelVisible: true, retentionDays: 7, profiles: [], rxBytes: 0, txBytes: 0, totalBytes: 0, loading: true, periodHours: initialAnalyticsPeriod });
  const awgAnalyticsPeriodRef = useRef(initialAnalyticsPeriod);
  const [securitySettings, setSecuritySettings] = useState({
    allowRememberMe: !!session?.settings?.allowRememberMe,
    enforceTwoFactor: !!session?.settings?.enforceTwoFactor,
    sessionDurationMinutes: session?.settings?.sessionDurationMinutes || 20,
    profileShareTtlMinutes: session?.settings?.profileShareTtlMinutes || 1440,
    analyticsEnabled: session?.settings?.analyticsEnabled ?? true,
    analyticsPanelVisible: session?.settings?.analyticsPanelVisible ?? true,
    analyticsRetentionDays: session?.settings?.analyticsRetentionDays || 7,
    defaultTheme: session?.settings?.theme || "light",
    defaultLocale: session?.settings?.locale || "ru",
    smtpHost: "",
    smtpPort: 587,
    smtpSecurity: "starttls",
    smtpUser: "",
    smtpPassword: "",
    smtpFromEmail: "",
    smtpFromName: "",
    passwordResetSubject: "",
    passwordResetBodyTemplate: "",
    loginVerificationSubject: "",
    loginVerificationBodyTemplate: "",
    profileLinkSubject: "",
    profileLinkBodyTemplate: "",
    invitationSubject: "",
    invitationBodyTemplate: "",
  });
  const [accountForm, setAccountForm] = useState({
    displayName: session?.settings?.displayName || "",
    email: session?.email || "",
    avatarUrl: session?.settings?.avatarUrl || "",
    twoFactorEnabled: !!session?.settings?.twoFactorEnabled,
  });
  const [accountInitial, setAccountInitial] = useState({
    displayName: session?.settings?.displayName || "",
    email: session?.email || "",
    avatarUrl: session?.settings?.avatarUrl || "",
    twoFactorEnabled: !!session?.settings?.twoFactorEnabled,
  });
  const [securityInitial, setSecurityInitial] = useState({
    allowRememberMe: !!session?.settings?.allowRememberMe,
    enforceTwoFactor: !!session?.settings?.enforceTwoFactor,
    sessionDurationMinutes: session?.settings?.sessionDurationMinutes || 20,
    profileShareTtlMinutes: session?.settings?.profileShareTtlMinutes || 1440,
    analyticsEnabled: session?.settings?.analyticsEnabled ?? true,
    analyticsPanelVisible: session?.settings?.analyticsPanelVisible ?? true,
    analyticsRetentionDays: session?.settings?.analyticsRetentionDays || 7,
    defaultTheme: session?.settings?.theme || "light",
    defaultLocale: session?.settings?.locale || "ru",
    smtpHost: "",
    smtpPort: 587,
    smtpSecurity: "starttls",
    smtpUser: "",
    smtpPassword: "",
    smtpFromEmail: "",
    smtpFromName: "",
    passwordResetSubject: "",
    passwordResetBodyTemplate: "",
    loginVerificationSubject: "",
    loginVerificationBodyTemplate: "",
    profileLinkSubject: "",
    profileLinkBodyTemplate: "",
    invitationSubject: "",
    invitationBodyTemplate: "",
  });

  const currentTheme = session?.settings?.theme || "light";
  const currentLocale = session?.settings?.locale || "ru";
  const isAdmin = !!session?.isAdmin;
  const isChangeNextDisabled = !oldPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword;

  useEffect(() => {
    const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY) || sessionStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!session?.settings && savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setCardScale(parsed.cardScale || 1);
      } catch {
        localStorage.removeItem(SETTINGS_STORAGE_KEY);
        sessionStorage.removeItem(SETTINGS_STORAGE_KEY);
      }
    } else if (typeof session?.settings?.cardScale === "number") {
      setCardScale(session.settings.cardScale);
    }
  }, [session]);

  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && typeof onSignOut === "function") {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          sessionStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(SETTINGS_STORAGE_KEY);
          sessionStorage.removeItem(SETTINGS_STORAGE_KEY);
          onSignOut();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptorId);
    };
  }, [onSignOut]);

  useEffect(() => {
    let cancelled = false;
    api.get("/update/notification", { headers: { "Accept-Language": currentLocale } })
      .then((response) => {
        if (!cancelled) setUpdateNotification(response.data?.notification || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentLocale]);

  const closeUpdateNotification = () => {
    const eventKey = updateNotification?.eventKey;
    setUpdateNotification(null);
    if (eventKey) {
      api.post("/update/notification/acknowledge", { eventKey }, { headers: { "Accept-Language": currentLocale } }).catch(() => {});
    }
  };

  useEffect(() => {
    if (!isAdmin || settingsOpen) return;
    loadAwgProfiles().catch(() => {});
    const intervalId = window.setInterval(() => {
      loadAwgProfiles().catch(() => {});
    }, 8000);
    return () => window.clearInterval(intervalId);
  }, [isAdmin, settingsOpen, currentLocale]);

  const updateSessionSettings = (settings) => {
    if (localStorage.getItem(TOKEN_STORAGE_KEY)) {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    }
    if (sessionStorage.getItem(TOKEN_STORAGE_KEY)) {
      sessionStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    }
    if (typeof onSessionUpdate === "function") {
      onSessionUpdate({ settings: { ...(session?.settings || {}), ...(settings || {}) } });
    }
  };

  const persistSettings = async (nextSettings) => {
    const response = await api.post("/settings", nextSettings, { headers: { "Accept-Language": currentLocale } });
    updateSessionSettings(response.data.settings);
    return response.data.settings;
  };

  const resetPasswordForm = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleCancel = () => {
    setChangePassOpen(false);
    setConfirmChangeOpen(false);
    resetPasswordForm();
  };

  const loadUsers = async () => {
    if (!isAdmin) return;
    const response = await api.get("/admin/users", { headers: { "Accept-Language": currentLocale } });
    setUsers(response.data.items || []);
  };

  const loadAwgProfiles = useCallback(async () => {
    if (!isAdmin) return;
    if (!awgProfilesLoadedOnce) setAwgProfilesLoading(true);
    try {
      const [profilesResponse, analyticsResponse] = await Promise.all([
        api.get("/admin/awg/profiles", { headers: { "Accept-Language": currentLocale } }),
        api.get("/admin/awg/analytics", { params: { periodHours: awgAnalyticsPeriodRef.current }, headers: { "Accept-Language": currentLocale } }),
      ]);
      setAwgProfiles(profilesResponse.data.items || []);
      setAwgAnalyticsSummary({ ...analyticsResponse.data, periodHours: awgAnalyticsPeriodRef.current, loading: false });
      setAwgProfilesLoadedOnce(true);
    } finally {
      setAwgProfilesLoading(false);
    }
  }, [isAdmin, currentLocale, awgProfilesLoadedOnce]);

  const changeAwgAnalyticsSummaryPeriod = async (periodHours) => {
    awgAnalyticsPeriodRef.current = periodHours;
    setAwgAnalyticsSummary((previous) => ({ ...previous, periodHours, loading: true }));
    try {
      const [response] = await Promise.all([
        api.get("/admin/awg/analytics", { params: { periodHours }, headers: { "Accept-Language": currentLocale } }),
        persistSettings({ analyticsPeriodHours: periodHours }),
      ]);
      setAwgAnalyticsSummary({ ...response.data, periodHours, loading: false });
    } catch (error) {
      setAwgAnalyticsSummary((previous) => ({ ...previous, loading: false }));
      setSnackbar({ open: true, message: error.response?.data?.error || (currentLocale === "en" ? "Failed to load analytics" : "Не удалось загрузить аналитику"), severity: "error" });
    }
  };

  const toggleAnalyticsDashboard = async () => {
    const previous = analyticsDashboardVisible;
    const next = !previous;
    setAnalyticsDashboardVisible(next);
    try {
      await persistSettings({ analyticsDashboardVisible: next });
    } catch {
      setAnalyticsDashboardVisible(previous);
      setSnackbar({ open: true, message: currentLocale === "en" ? "Failed to save dashboard visibility" : "Не удалось сохранить отображение аналитики", severity: "error" });
    }
  };

  const handleChangePasswordNext = () => {
    setChangePassOpen(false);
    setConfirmChangeOpen(true);
  };

  const handleConfirmChangePassword = async () => {
    setConfirmChangeOpen(false);
    setLoading(true);

    try {
      await api.post("/change_password", { old_password: oldPassword, new_password: newPassword }, { headers: { "Accept-Language": currentLocale } });
      setSnackbar({ open: true, message: t("password.changed", currentLocale), severity: "success" });
      handleCancel();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("password.changeFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleThemeToggle = async () => {
    const nextTheme = currentTheme === "dark" ? "light" : "dark";
    try {
      await persistSettings({ theme: nextTheme });
      setSnackbar({ open: true, message: t("common.themeSaved", currentLocale), severity: "success" });
    } catch {
      setSnackbar({ open: true, message: t("common.themeSaveFailed", currentLocale), severity: "error" });
    }
  };

  const handleLocaleChange = async (locale) => {
    try {
      await persistSettings({ locale });
    } catch {
      setSnackbar({ open: true, message: t("common.themeSaveFailed", currentLocale), severity: "error" });
    }
  };

  const handleCreateUserFieldChange = (field, value) => {
    setCreateUserForm((prev) => ({ ...prev, [field]: value }));
  };

  const openNewUser = () => setCreateUserForm((prev) => ({ ...prev, open: true }));
  const closeNewUser = () => setCreateUserForm({ open: false, displayName: "", email: "", role: "user", password: "", confirmPassword: "", sendInvitation: false });
  const openPasswordDialog = (email) => setPasswordDialog({ open: true, email, password: "", confirmPassword: "" });
  const closePasswordDialog = () => setPasswordDialog({ open: false, email: "", password: "", confirmPassword: "" });
  const handlePasswordFieldChange = (email, value) => setPasswordDialog((prev) => ({ ...prev, email: email || prev.email, password: value }));
  const handlePasswordConfirmChange = (email, value) => setPasswordDialog((prev) => ({ ...prev, email: email || prev.email, confirmPassword: value }));
  const openDeleteDialog = (email) => setDeleteUserDialog({ open: true, email });
  const closeDeleteDialog = () => setDeleteUserDialog({ open: false, email: "" });
  const openAwgCreateDialog = () => setAwgCreateDialog({ open: true, profileName: "", displayName: "", email: "", sendEmail: false });
  const closeAwgCreateDialog = () => setAwgCreateDialog({ open: false, profileName: "", displayName: "", email: "", sendEmail: false });
  const openAwgEditDialog = (profile) => setAwgEditDialog({ open: true, profileName: profile?.profileName || "", displayName: profile?.displayName || "", email: profile?.email || "" });
  const closeAwgEditDialog = () => setAwgEditDialog({ open: false, profileName: "", displayName: "", email: "" });
  const openAwgDeleteDialog = (profileName) => setAwgDeleteDialog({ open: true, profileName });
  const closeAwgDeleteDialog = () => setAwgDeleteDialog({ open: false, profileName: "" });
  const openAwgEventsDialog = async (profileName, periodHours = 24) => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/awg/profiles/${encodeURIComponent(profileName)}/events`, {
        params: { periodHours },
        headers: { "Accept-Language": currentLocale },
      });
      setAwgEventsDialog({ open: true, profileName, items: response.data?.items || [], periodHours });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("shell.eventsLoadFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };
  const closeAwgEventsDialog = () => setAwgEventsDialog({ open: false, profileName: "", items: [], periodHours: 24 });
  const closeAwgShareDialog = () => setAwgShareDialog({ open: false, profileName: "", displayName: "", email: "" });
  const openAwgAnalyticsDialog = async (profile, periodHours = "all") => {
    setAwgAnalyticsDialog({ open: true, profile, data: null, loading: true, periodHours });
    try {
      const response = await api.get(`/admin/awg/profiles/${encodeURIComponent(profile?.profileName || "")}/analytics`, { params: { periodHours }, headers: { "Accept-Language": currentLocale } });
      setAwgAnalyticsDialog({ open: true, profile, data: response.data, loading: false, periodHours });
    } catch (error) {
      setAwgAnalyticsDialog({ open: false, profile: null, data: null, loading: false, periodHours: "all" });
      setSnackbar({ open: true, message: error.response?.data?.error || (currentLocale === "en" ? "Failed to load analytics" : "Не удалось загрузить аналитику"), severity: "error" });
    }
  };
  const closeAwgAnalyticsDialog = () => setAwgAnalyticsDialog({ open: false, profile: null, data: null, loading: false, periodHours: "all" });
  const handleDeleteUser = async () => {
    if (!deleteUserDialog.email) return;
    if ((deleteUserDialog.email || "").toLowerCase() === (session?.email || "").toLowerCase()) {
      setSnackbar({ open: true, message: t("admin.cannotDeleteSelf", currentLocale), severity: "error" });
      return;
    }
    setLoading(true);
    try {
      await api.delete(`/admin/users/${encodeURIComponent(deleteUserDialog.email)}`, { headers: { "Accept-Language": currentLocale } });
      await loadUsers();
      closeDeleteDialog();
      setSnackbar({ open: true, message: t("admin.userDeleted", currentLocale), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("admin.userDeleteFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadSecuritySettings = async () => {
    if (!isAdmin) return;
    const response = await api.get("/admin/security", { headers: { "Accept-Language": currentLocale } });
    const next = response.data.settings || { allowRememberMe: false, enforceTwoFactor: false, sessionDurationMinutes: 20, profileShareTtlMinutes: 1440, defaultTheme: "light", defaultLocale: "ru", smtpHost: "", smtpPort: 587, smtpSecurity: "starttls", smtpUser: "", smtpPassword: "", smtpFromEmail: "", smtpFromName: "", passwordResetSubject: "", passwordResetBodyTemplate: "", loginVerificationSubject: "", loginVerificationBodyTemplate: "", profileLinkSubject: "", profileLinkBodyTemplate: "", invitationSubject: "", invitationBodyTemplate: "" };
    setSecuritySettings(next);
    setSecurityInitial(next);
  };

  const openAccountPanel = async () => {
    const next = {
      displayName: session?.settings?.displayName ?? "",
      email: session?.email || "",
      avatarUrl: session?.settings?.avatarUrl || "",
      twoFactorEnabled: !!session?.settings?.twoFactorEnabled,
    };
    setAccountForm(next);
    setAccountInitial(next);
    setAccountOpen(true);
  };

  const openAdminPanel = async (initialSection = "general") => {
    if (!isAdmin) {
      return;
    }
    const nextSection = !adminSectionInitialized ? "general" : (initialSection || "general");
    setSettingsSection(nextSection);
    setAdminSectionInitialized(true);
    setSettingsOpen(true);
    setLoading(true);
    try {
      await Promise.all([loadUsers(), loadAwgProfiles(), loadSecuritySettings()]);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("admin.usersLoadFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const closeAdminPanel = () => {
    setSettingsOpen(false);
    setSettingsSection("general");
    closeNewUser();
    closePasswordDialog();
    closeDeleteDialog();
    closeAwgCreateDialog();
    closeAwgEditDialog();
    closeAwgDeleteDialog();
  };

  const closeAccountPanel = () => {
    setAccountForm(accountInitial);
    setAccountOpen(false);
  };

  useEffect(() => {
    setAccountOpen(false);
  }, [session?.email]);

  useEffect(() => {
    const next = {
      displayName: session?.settings?.displayName ?? "",
      email: session?.email || "",
      avatarUrl: session?.settings?.avatarUrl || "",
      twoFactorEnabled: !!session?.settings?.twoFactorEnabled,
    };
    setAccountForm(next);
    setAccountInitial(next);
  }, [session?.email, session?.settings?.displayName, session?.settings?.avatarUrl, session?.settings?.twoFactorEnabled]);

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setAccountForm((prev) => ({ ...prev, avatarUrl: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarRemove = async () => {
    setAccountForm((prev) => ({ ...prev, avatarUrl: "" }));
  };

  const handleCreateUser = async () => {
    if (!createUserForm.displayName?.trim() || !createUserForm.email || (!createUserForm.sendInvitation && !createUserForm.password)) {
      setSnackbar({ open: true, message: t("admin.fillEmailPassword", currentLocale), severity: "error" });
      return;
    }
    if (!createUserForm.sendInvitation && createUserForm.password !== createUserForm.confirmPassword) {
      setSnackbar({ open: true, message: t("admin.passwordsMismatch", currentLocale), severity: "error" });
      return;
    }

    setLoading(true);
    try {
      await api.post("/admin/users", {
        email: createUserForm.email,
        displayName: createUserForm.displayName,
        role: createUserForm.role,
        password: createUserForm.password,
        sendInvitation: !!createUserForm.sendInvitation,
      }, { headers: { "Accept-Language": currentLocale } });
      await loadUsers();
      closeNewUser();
      setSnackbar({ open: true, message: t("admin.userCreated", currentLocale), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("admin.userCreateFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAdminRoleChange = async (email, role) => {
    setLoading(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(email)}/role`, { role }, { headers: { "Accept-Language": currentLocale } });
      await loadUsers();
      setSnackbar({ open: true, message: t("admin.roleUpdated", currentLocale), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("admin.roleUpdateFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAdminPasswordSave = async (email = passwordDialog.email) => {
    const password = passwordDialog.password;
    if (!password || password !== passwordDialog.confirmPassword) {
      setSnackbar({ open: true, message: t("admin.passwordsMismatch", currentLocale), severity: "error" });
      return;
    }

    setLoading(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(email)}/password`, { password }, { headers: { "Accept-Language": currentLocale } });
      await loadUsers();
      closePasswordDialog();
      setSnackbar({ open: true, message: t("admin.passwordUpdated", currentLocale), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("admin.passwordUpdateFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAdminSecurityToggle = async (email, allowRememberMe) => {
    setLoading(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(email)}/security`, { allowRememberMe }, { headers: { "Accept-Language": currentLocale } });
      await loadUsers();
      setSnackbar({ open: true, message: t("admin.securityUpdated", currentLocale), severity: "success" });
      if (session?.email?.toLowerCase() === email.toLowerCase()) {
        await persistSettings({ allowRememberMe });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("admin.securityUpdateFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAccountFieldChange = (field, value) => {
    setAccountForm((prev) => ({ ...prev, [field]: value }));
  };


  const handleAccountSave = async () => {
    setLoading(true);
    try {
      const response = await api.post("/settings", { displayName: accountForm.displayName, avatarUrl: accountForm.avatarUrl, twoFactorEnabled: accountForm.twoFactorEnabled }, { headers: { "Accept-Language": currentLocale } });
      const next = {
        displayName: response.data.settings?.displayName ?? accountForm.displayName ?? "",
        email: session?.email || "",
        avatarUrl: response.data.settings?.avatarUrl ?? accountForm.avatarUrl ?? "",
        twoFactorEnabled: !!(response.data.settings?.twoFactorEnabled ?? accountForm.twoFactorEnabled),
      };
      setAccountForm(next);
      setAccountInitial(next);
      if (typeof onSessionUpdate === "function") {
        onSessionUpdate({
          email: session?.email,
          settings: { ...(session?.settings || {}), ...(response.data.settings || {}), displayName: next.displayName, avatarUrl: next.avatarUrl, twoFactorEnabled: next.twoFactorEnabled },
        });
      }
      setAccountInitial(next);
      setSnackbar({ open: true, message: t("settings.accountSaved", currentLocale), severity: "success" });
      setAccountOpen(false);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("settings.accountSaveFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySettingsChange = (field, value) => {
    setSecuritySettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecuritySettingsSave = async (override = null) => {
    const payload = override ? { ...securitySettings, ...override } : securitySettings;
    setLoading(true);
    try {
      const response = await api.put("/admin/security", payload, { headers: { "Accept-Language": currentLocale } });
      const next = response.data.settings || payload;
      setSecuritySettings(next);
      setSecurityInitial(next);
      if (typeof onSessionUpdate === "function") {
        onSessionUpdate({ settings: { ...(session?.settings || {}), ...(response.data.settings || {}) } });
      }
      setSnackbar({ open: true, message: settingsSection === "analytics" ? (currentLocale === "en" ? "Analytics settings saved" : "Настройки аналитики сохранены") : t("settings.securitySaved", currentLocale), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("settings.securitySaveFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEmailSettings = async () => {
    setLoading(true);
    try {
      await api.post("/admin/email/check", {}, { headers: { "Accept-Language": currentLocale } });
      setSnackbar({ open: true, message: currentLocale === "en" ? "SMTP connection succeeded" : "SMTP-подключение успешно проверено", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || (currentLocale === "en" ? "Failed to verify SMTP settings" : "Не удалось проверить SMTP-настройки"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async (email) => {
    setLoading(true);
    try {
      await api.post("/admin/email/test", { email, subject: securitySettings.passwordResetSubject, bodyTemplate: securitySettings.passwordResetBodyTemplate }, { headers: { "Accept-Language": currentLocale } });
      setSnackbar({ open: true, message: currentLocale === "en" ? "Test email sent" : "Тестовое письмо отправлено", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || (currentLocale === "en" ? "Failed to send test email" : "Не удалось отправить тестовое письмо"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const slugifyProfileName = (value) => {
    const map = {
      а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
    };
    const transliterated = String(value || '')
      .toLowerCase()
      .split('')
      .map((ch) => map[ch] ?? ch)
      .join('');
    return transliterated
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  };

  const handleAwgCreateFieldChange = (field, value) => {
    setAwgCreateDialog((prev) => {
      if (field === 'displayName') {
        return { ...prev, displayName: value, profileName: slugifyProfileName(value) };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleAwgEditFieldChange = (field, value) => {
    setAwgEditDialog((prev) => ({ ...prev, [field]: value }));
  };

  const handleAwgCreate = async () => {
    if (!awgCreateDialog.profileName || (awgCreateDialog.sendEmail && !awgCreateDialog.email)) {
      setSnackbar({ open: true, message: t("shell.fillProfileName", currentLocale), severity: "error" });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post("/admin/awg/profiles", { profileName: awgCreateDialog.profileName, displayName: awgCreateDialog.displayName, email: awgCreateDialog.email, sendEmail: awgCreateDialog.sendEmail }, { headers: { "Accept-Language": currentLocale } });
      await loadAwgProfiles();
      closeAwgCreateDialog();
      const emailFailed = awgCreateDialog.sendEmail && !response.data?.emailSent;
      setSnackbar({ open: true, message: emailFailed ? (response.data?.emailError || (currentLocale === "en" ? "Profile created, but email was not sent" : "Профиль создан, но письмо не отправлено")) : t("shell.createProfileSuccess", currentLocale), severity: emailFailed ? "warning" : "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("shell.createProfileFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAwgEditSave = async () => {
    if (!awgEditDialog.profileName) return;
    setLoading(true);
    try {
      await api.put(`/admin/awg/profiles/${encodeURIComponent(awgEditDialog.profileName)}`, { displayName: awgEditDialog.displayName, email: awgEditDialog.email }, { headers: { "Accept-Language": currentLocale } });
      await loadAwgProfiles();
      closeAwgEditDialog();
      setSnackbar({ open: true, message: t("shell.updateProfileSuccess", currentLocale), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("shell.updateProfileFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAwgDelete = async () => {
    if (!awgDeleteDialog.profileName) return;
    setLoading(true);
    try {
      await api.delete(`/admin/awg/profiles/${encodeURIComponent(awgDeleteDialog.profileName)}`, { headers: { "Accept-Language": currentLocale } });
      await loadAwgProfiles();
      closeAwgDeleteDialog();
      setSnackbar({ open: true, message: t("shell.deleteProfileSuccess", currentLocale), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("shell.deleteProfileFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAwgDownload = async (profileName) => {
    try {
      const response = await api.get(`/admin/awg/profiles/${encodeURIComponent(profileName)}/download`, { responseType: "blob", headers: { "Accept-Language": currentLocale } });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ocean.conf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t("shell.downloadProfileFailed", currentLocale), severity: "error" });
    }
  };

  const createAwgShare = async (sendEmail = false) => {
    const profileName = awgShareDialog.profileName;
    if (!profileName) throw new Error("Missing profile name");
    const response = await api.post(`/admin/awg/profiles/${encodeURIComponent(profileName)}/share`, { sendEmail }, { headers: { "Accept-Language": currentLocale } });
    const url = response.data?.item?.url;
    if (!url) throw new Error("Missing share url");
    return url;
  };

  const copyText = async (value) => {
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch {}
    }
    if (typeof document === "undefined") return false;
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  };

  const handleAwgShare = (profile) => {
    setAwgShareDialog({ open: true, profileName: profile?.profileName || "", displayName: profile?.displayName || "", email: profile?.email || "" });
  };

  const handleAwgShareCopy = async () => {
    setLoading(true);
    try {
      const url = await createAwgShare(false);
      if (!(await copyText(url))) throw new Error(url);
      closeAwgShareDialog();
      setSnackbar({ open: true, message: t("shell.shareProfileSuccess", currentLocale), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || error.message || t("shell.shareProfileFailed", currentLocale), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAwgShareEmail = async () => {
    setLoading(true);
    try {
      await createAwgShare(true);
      closeAwgShareDialog();
      setSnackbar({ open: true, message: currentLocale === "en" ? "Email sent" : "Письмо отправлено", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || error.message || (currentLocale === "en" ? "Failed to send email" : "Не удалось отправить письмо"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const accountDirty = (accountForm.displayName || "").trim() !== (accountInitial.displayName || "").trim() || (accountForm.avatarUrl || "") !== (accountInitial.avatarUrl || "") || String(!!accountForm.twoFactorEnabled) !== String(!!accountInitial.twoFactorEnabled);
  const securityDirty = String(securitySettings.allowRememberMe) !== String(securityInitial.allowRememberMe)
    || String(!!securitySettings.enforceTwoFactor) !== String(!!securityInitial.enforceTwoFactor)
    || String(securitySettings.sessionDurationMinutes ?? "") !== String(securityInitial.sessionDurationMinutes ?? "")
    || String(securitySettings.profileShareTtlMinutes ?? "") !== String(securityInitial.profileShareTtlMinutes ?? "")
    || String(!!securitySettings.analyticsEnabled) !== String(!!securityInitial.analyticsEnabled)
    || String(!!securitySettings.analyticsPanelVisible) !== String(!!securityInitial.analyticsPanelVisible)
    || String(securitySettings.analyticsRetentionDays ?? "") !== String(securityInitial.analyticsRetentionDays ?? "")
    || String(securitySettings.defaultTheme ?? "") !== String(securityInitial.defaultTheme ?? "")
    || String(securitySettings.defaultLocale ?? "") !== String(securityInitial.defaultLocale ?? "")
    || String(securitySettings.smtpHost ?? "") !== String(securityInitial.smtpHost ?? "")
    || String(securitySettings.smtpPort ?? "") !== String(securityInitial.smtpPort ?? "")
    || String(securitySettings.smtpSecurity ?? "starttls") !== String(securityInitial.smtpSecurity ?? "starttls")
    || String(securitySettings.smtpUser ?? "") !== String(securityInitial.smtpUser ?? "")
    || String(securitySettings.smtpPassword ?? "") !== String(securityInitial.smtpPassword ?? "")
    || String(securitySettings.smtpFromEmail ?? "") !== String(securityInitial.smtpFromEmail ?? "")
    || String(securitySettings.smtpFromName ?? "") !== String(securityInitial.smtpFromName ?? "")
    || String(securitySettings.passwordResetSubject ?? "") !== String(securityInitial.passwordResetSubject ?? "")
    || String(securitySettings.passwordResetBodyTemplate ?? "") !== String(securityInitial.passwordResetBodyTemplate ?? "")
    || String(securitySettings.loginVerificationSubject ?? "") !== String(securityInitial.loginVerificationSubject ?? "")
    || String(securitySettings.loginVerificationBodyTemplate ?? "") !== String(securityInitial.loginVerificationBodyTemplate ?? "")
    || String(securitySettings.profileLinkSubject ?? "") !== String(securityInitial.profileLinkSubject ?? "")
    || String(securitySettings.profileLinkBodyTemplate ?? "") !== String(securityInitial.profileLinkBodyTemplate ?? "")
    || String(securitySettings.invitationSubject ?? "") !== String(securityInitial.invitationSubject ?? "")
    || String(securitySettings.invitationBodyTemplate ?? "") !== String(securityInitial.invitationBodyTemplate ?? "");

  const resetAccountForm = () => {
    setAccountForm(accountInitial);
  };

  const resetSecurityForm = () => {
    setSecuritySettings(securityInitial);
  };

  return {
    loading,
    snackbar,
    updateNotification,
    changePassOpen,
    confirmChangeOpen,
    oldPassword,
    newPassword,
    confirmPassword,
    cardScale,
    isChangeNextDisabled,
    currentTheme,
    currentLocale,
    isAdmin,
    settingsOpen,
    accountOpen,
    settingsSection,
    users,
    awgProfiles,
    awgProfilesLoading,
    awgProfilesLoadedOnce,
    createUserForm,
    passwordDialog,
    deleteUserDialog,
    awgCreateDialog,
    awgEditDialog,
    awgDeleteDialog,
    awgEventsDialog,
    awgShareDialog,
    awgAnalyticsDialog,
    awgAnalyticsSummary,
    analyticsDashboardVisible,
    securitySettings,
    accountForm,
    accountDirty,
    securityDirty,
    setChangePassOpen,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    setSettingsSection,
    handleCancel,
    handleChangePasswordNext,
    handleConfirmChangePassword,
    handleCloseSnackbar,
    closeUpdateNotification,
    handleThemeToggle,
    handleLocaleChange,
    toggleAnalyticsDashboard,
    openAdminPanel,
    openAccountPanel,
    closeAdminPanel,
    closeAccountPanel,
    openNewUser,
    closeNewUser,
    openPasswordDialog,
    closePasswordDialog,
    handlePasswordFieldChange,
    handlePasswordConfirmChange,
    openDeleteDialog,
    closeDeleteDialog,
    handleDeleteUser,
    handleCreateUserFieldChange,
    handleCreateUser,
    handleAdminRoleChange,
    handleAdminPasswordSave,
    handleAdminSecurityToggle,
    handleSecuritySettingsChange,
    handleSecuritySettingsSave,
    handleAccountFieldChange,
    handleAccountSave,
    resetAccountForm,
    resetSecurityForm,
    handleAvatarUpload,
    handleAvatarRemove,
    handleCheckEmailSettings,
    handleSendTestEmail,
    openAwgCreateDialog,
    closeAwgCreateDialog,
    openAwgEditDialog,
    closeAwgEditDialog,
    openAwgDeleteDialog,
    closeAwgDeleteDialog,
    openAwgEventsDialog,
    closeAwgEventsDialog,
    closeAwgShareDialog,
    openAwgAnalyticsDialog,
    changeAwgAnalyticsSummaryPeriod,
    closeAwgAnalyticsDialog,
    handleAwgCreateFieldChange,
    handleAwgEditFieldChange,
    handleAwgCreate,
    handleAwgEditSave,
    handleAwgDelete,
    handleAwgDownload,
    handleAwgShare,
    handleAwgShareCopy,
    handleAwgShareEmail,
    refreshAwgProfiles: loadAwgProfiles,
  };
}
